import React, { useMemo } from "react";
import { Button } from "react-bootstrap";
import { Gpt } from "../base/Gpt";
import { MISSION_CONFIG } from "./missionConfig";

function styledTitle(topLabel, subtitle) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.05 }}>
      <span style={{ fontStyle: "italic", fontSize: "0.6em", opacity: 0.7, whiteSpace: "nowrap" }}>{topLabel}</span>
      <span style={{ fontSize: "0.95em" }}>{subtitle}</span>
    </span>
  );
}

export default function MissionGpt(props) {
  const missionName = props.missionData ? props.missionData.name : 'Unknown Mission';
  const missionYear = props.missionData ? props.missionData.date : '';
  const missionSignificance = props.missionData ? props.missionData.significance : '';

  const missionGptConfig = useMemo(() => {
    const base = MISSION_CONFIG.gpt;
    const originalGetPrompts = base.getPrompts;
    const basePostVideo = base.postVideoPrompts || [];
    return {
      ...base,
      filmIdeas: [missionName],
      postVideoPrompts: basePostVideo.map(pvp => {
        if (pvp.title === "Training")
          return { ...pvp, promptVal: [`Create a detailed training plan for the ${missionName} mission. If the mission is crewed, include crew physical fitness requirements, spacecraft systems training, mission-specific simulations, emergency procedures, and EVA preparation. If the mission is uncrewed, include ground team training, remote operations procedures, autonomous systems testing, and contingency response drills. Include a training timeline from team selection to launch.`] };
        if (pvp.title === "Parts List")
          return { ...pvp, promptVal: [`Create a complete parts list for the ${missionName} next generation spacecraft design. Include all major systems: propulsion, life support, power, communications, navigation, thermal control, and structural components. For each part, list the component name, quantity, specification, cost, and supplier.`] };
        return pvp;
      }),
      getPrompts: (args) => {
        const prompts = originalGetPrompts(args);
        prompts[0] = {
          ...prompts[0],
          title: styledTitle(missionName, "Mission Overview"),
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    <span style={{ fontWeight: "bold", fontSize: "1.1em" }}>Let's see what AI thinks the <span style={{ color: "#f05f40" }}>{missionName}</span> spacecraft looks like.</span>
                    <br /><br />
                    Use the{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 1)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      2: Mission Image
                    </Button>{" "}
                    prompt to create a detailed image description for an image generation model.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[1] = { ...prompts[1], title: styledTitle(missionName, "Mission Image"), fillIn: missionName, noAutoAdvance: true, autoPopulateDalle: true };
        prompts[2] = {
          ...prompts[2],
          title: styledTitle(missionName, <span style={{ whiteSpace: "nowrap" }}>Mission Transmission</span>),
          fillIn: missionName,
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Now bring your transmission to life. Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.setActiveTab("voice-cloning")} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                      Voice
                    </Button>{" "}
                    to turn your script into realistic spoken audio.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[3] = {
          ...prompts[3],
          title: styledTitle("Next Gen", "Mission Design"),
          fillIn: "Type your Next Gen mission objective here...",
          wrapFillInSlot: true,
          promptVal: [
            `You are leading the Next Gen ${missionName} mission. Building on the ${missionSignificance}, describe your mission's primary objective.\n\n`,
            `<br /><br /><span class="gpt-prompt-hint-muted">AI: Describe the Next Gen technologies required for the power, communication, navigation, scientific, and launch systems your mission needs to succeed.</span>`,
          ],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 4)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      5: Mission Budget
                    </Button>{" "}
                    to create a detailed budget breakdown for your <span style={{ fontWeight: "300", fontStyle: "italic" }}>Next Generation</span> mission.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[4] = {
          ...prompts[4],
          title: styledTitle("Next Gen", "Mission Budget"),
          budgetAllocation: true,
          fillIn: undefined,
          promptVal: [""],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 5)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      6: ROI Analysis
                    </Button>{" "}
                    to justify your <span style={{ fontWeight: "300", fontStyle: "italic" }}>Next Gen</span> investment to Congress.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        const roiFillText = "Explain why your mission deserves funding, what it will achieve, and why the investment is worth it...";
        const roiFillEnc = encodeURIComponent(roiFillText);
        const roiFillEsc = roiFillText.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
        prompts[5] = {
          ...prompts[5],
          title: styledTitle("Next Gen", "ROI Analysis"),
          fillIn: undefined,
          wrapFillInSlot: false,
          promptVal: [
            `The future of the Next Gen ${missionName} mission is in your hands. As mission director, you must persuade Congress to approve funding for your project.<br /><span class="gpt-fill-in-slot gpt-roi-fill-box" data-fill-in-encoded="${roiFillEnc}">${roiFillEsc}</span><br /><span class="gpt-prompt-hint-muted">AI: You are a U.S. congressional funding committee. Review the mission proposal, explain your reasoning, and make a Yes or No funding decision.</span>`,
          ],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.handlePromptButtonClicked(null, 6)} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease" }}>
                      7: Spacecraft Image
                    </Button>{" "}
                    to generate an image description of your <span style={{ fontWeight: "300", fontStyle: "italic" }}>Next Generation</span>{" "}
                    <span style={{ color: "#f05f40", fontWeight: "bold" }}>{missionName}</span> spacecraft.
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        prompts[6] = {
          ...prompts[6],
          title: styledTitle("Next Gen", "Spacecraft Image"),
          fillIn: missionName,
          autoPopulateDalle: true,
          promptVal: [
            "Write an AI image prompt for a Next Gen ",
            " spacecraft with visible upgraded hardware, set against a simple space background in a documentary photo style.",
          ],
          showAfter: (
            <>
              <hr />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ backgroundColor: "white", fontSize: "large", padding: "10px 15px 15px 15px", borderRadius: "15px", border: "2px solid #f05f40", boxShadow: "0 0 15px rgba(240, 95, 64, 0.6)" }}>
                  <h3 style={{ color: "#999", fontWeight: "bold", margin: "0 0 5px 0", fontSize: "12px", fontStyle: "italic" }}>Instructions:</h3>
                  <p style={{ margin: "0" }}>
                    Now generate an image of your <em>Next Generation</em> <span style={{ color: "#f05f40", fontWeight: "bold" }}>{missionName}</span> mission.
                    <br /><br />
                    Select{" "}
                    <Button variant="outline-primary" size="sm" onClick={() => args.setActiveTab("dalle")} style={{ margin: "0 5px", padding: "4px 12px", fontSize: "14px", display: "inline-block", whiteSpace: "nowrap", flexShrink: 0, transition: "all 0.3s ease", borderColor: "#f05f40", color: "#f05f40" }}>
                      Image
                    </Button>
                  </p>
                </div>
              </div>
              <hr />
            </>
          ),
        };
        return prompts;
      },
    };
  }, [missionName, missionYear, missionSignificance]);

  return (
    <Gpt
      {...props}
      workshopConfig={missionGptConfig}
      storageKey={MISSION_CONFIG.storageKeys.gptMessages}
    />
  );
}
